import { AnalyticsUserEvent } from '@fruitful/types';
import { LinearGradient } from 'expo-linear-gradient';
import { Redirect, router } from 'expo-router';
import { useSetAtom } from 'jotai';
import * as React from 'react';
import {
    BodyLargeRegular,
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    FruitfulCustomIcon,
    FullScreenSpinner,
    H2Emphasized,
    H3Emphasized,
    H5Regular,
    Icon,
    ScreenContainer,
    SplitIcon,
    View,
    XStack,
    YStack,
} from 'smoothie';

import useGetDirectDepositAdoption from '../../../bankingSwitch/hooks/useGetDirectDepositAdoption';
import { track } from '../../../logger/Logger';
import useGetActiveMoneyMap from '../../../roadmap/hooks/useGetActiveMoneyMap';
import useShouldSeeIncomeSplit from '../../hooks/income-split/useShouldSeeIncomeSplit';
import useGetAccountDisplayDetailsFromGlobalState from '../../hooks/useGetAccountDisplayDetailsFromGlobalState';
import { selectedAccountForDirectDepositAtom } from '../../state/SpacesAtoms';
import type { IncomeSplitContext } from '../../types/IncomeSplit';

const AccountBox = ({ label, color }: { label: string; color: string }) => {
    return (
        <XStack
            width="100%"
            padding={12}
            borderRadius={16}
            backgroundColor="$gray200"
            alignItems="center"
            gap={10}
        >
            <FruitfulCustomIcon size="xs" color={color} icon="fruitful" />
            <BodyMediumRegular>{label}</BodyMediumRegular>
        </XStack>
    );
};

interface IncomeSplitConfirmationProps {
    isEdit: boolean;
    context: IncomeSplitContext;
}
const IncomeSplitConfirmation = ({
    isEdit,
    context,
}: IncomeSplitConfirmationProps) => {
    const shouldSeeIncomeSplit = useShouldSeeIncomeSplit();

    const setAccountForDirectDeposit = useSetAtom(
        selectedAccountForDirectDepositAtom,
    );

    const { moneyMap, isLoadingMoneyMap } = useGetActiveMoneyMap();
    const incomeAccountId = moneyMap?.incomeMoneyMapAccount?.bankAccountId;
    const { account: incomeAccount } =
        useGetAccountDisplayDetailsFromGlobalState(
            {
                bankAccountId: incomeAccountId ?? '',
            },
            !!moneyMap && incomeAccountId != null,
        );

    const { isDirectDepositAdoptionComplete, isLoadingDirectDepositAdoption } =
        useGetDirectDepositAdoption();

    const isGoButtonFlow = context === 'goButtonFlow';

    const shouldUseDirectDepositFlow = React.useMemo(() => {
        return isGoButtonFlow && !isDirectDepositAdoptionComplete;
    }, [isGoButtonFlow, isDirectDepositAdoptionComplete]);

    const backUrl = '/home';
    const backButtonLabel = isGoButtonFlow
        ? shouldUseDirectDepositFlow
            ? 'Move your paycheck'
            : 'Done'
        : 'Back to Fruitful Cash';

    if (!shouldSeeIncomeSplit) return <Redirect href="/" />;

    if (isLoadingMoneyMap || isLoadingDirectDepositAdoption) {
        return <FullScreenSpinner />;
    }

    return (
        <ScreenContainer
            hasCloseButton={true}
            onClose={() => {
                track(AnalyticsUserEvent.INCOME_SPLIT_SUCCESS_INTERACTION, {
                    action: 'close',
                });
                router.navigate(backUrl);
            }}
            title={shouldUseDirectDepositFlow ? 'Direct Deposit' : undefined}
        >
            <YStack gap={24} justifyContent="space-between">
                <YStack justifyContent="space-between" paddingTop={56}>
                    {shouldUseDirectDepositFlow ? (
                        <YStack width="100%" gap={40} alignItems="center">
                            <YStack width="100%" gap={8} alignItems="center">
                                <H5Regular textAlign="center">
                                    Start funding your system
                                </H5Regular>
                                <BodyLargeRegular
                                    textAlign="center"
                                    opacity={0.66}
                                >
                                    Connect direct deposit to start auto-funding
                                    your bills, spend, and goals
                                </BodyLargeRegular>
                            </YStack>
                            <YStack
                                width="50%"
                                gap={8}
                                alignItems="center"
                                position="relative"
                            >
                                <View
                                    justifyContent="center"
                                    alignItems="center"
                                    padding={16}
                                    borderRadius={16}
                                    backgroundColor="$lemon"
                                >
                                    <BodyLargeRegular>
                                        Your paycheck
                                    </BodyLargeRegular>
                                </View>
                                <View
                                    width={4}
                                    borderRadius={24}
                                    backgroundColor="$lemonLightest"
                                    height={42}
                                >
                                    <View
                                        width="100%"
                                        borderRadius={24}
                                        backgroundColor="$lemon"
                                        height={16}
                                    />
                                </View>
                                <AccountBox label="Core" color="blue" />
                                <AccountBox label="Spend" color="green" />
                                <AccountBox
                                    label="Emergency Fund"
                                    color="red"
                                />
                                <AccountBox
                                    label="Personal Goal"
                                    color="peach"
                                />
                                <LinearGradient
                                    colors={[
                                        'rgba(255, 255, 255, 0)',
                                        '#FFFFFF',
                                    ]}
                                    locations={[0, 0.8804]}
                                    start={{ x: 0.5, y: 0 }}
                                    end={{ x: 0.5, y: 1 }}
                                    pointerEvents="none"
                                    style={{
                                        position: 'absolute',
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        width: '100%',
                                        height: 184,
                                    }}
                                />
                            </YStack>
                        </YStack>
                    ) : (
                        <YStack
                            gap={24}
                            paddingHorizontal={24}
                            justifyContent="center"
                            alignItems="center"
                            width="100%"
                        >
                            <View
                                padding={20}
                                borderRadius={16}
                                backgroundColor="$gray300"
                                alignItems="center"
                                justifyContent="center"
                                transform={[{ rotate: '180deg' }]}
                            >
                                <Icon icon={SplitIcon} size={32} />
                            </View>
                            <YStack gap={24}>
                                {isGoButtonFlow ? (
                                    <H3Emphasized textAlign="center">
                                        Your Income Split rules are live!
                                    </H3Emphasized>
                                ) : isEdit ? (
                                    <H2Emphasized textAlign="center">
                                        Your Income Split has been updated!
                                    </H2Emphasized>
                                ) : (
                                    <H2Emphasized textAlign="center">
                                        Your Income Split is now enabled!
                                    </H2Emphasized>
                                )}
                                <BodyMediumRegular
                                    textAlign="center"
                                    color="$gray1000"
                                >
                                    You&apos;ll receive an email each time your
                                    rules are executed. You can turn off or
                                    change your Income Split at any time.
                                </BodyMediumRegular>
                            </YStack>
                        </YStack>
                    )}
                </YStack>
                <YStack alignItems="center" gap={16} width="100%">
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.LARGE}
                        text={backButtonLabel}
                        onPress={() => {
                            if (shouldUseDirectDepositFlow) {
                                track(
                                    AnalyticsUserEvent.INCOME_SPLIT_SUCCESS_INTERACTION,
                                    { action: 'set-up-direct-deposit' },
                                );
                                setAccountForDirectDeposit(incomeAccount);
                                router.navigate({
                                    pathname: '/banking/direct-deposit/switch',
                                    params: {
                                        context: 'roadmap',
                                        source: 'money-map-income-split',
                                    },
                                });
                            } else {
                                track(
                                    AnalyticsUserEvent.INCOME_SPLIT_SUCCESS_INTERACTION,
                                    {
                                        action: 'back',
                                    },
                                );
                                router.navigate(backUrl);
                            }
                        }}
                    />
                    {shouldUseDirectDepositFlow && (
                        <BodySmallRegular color="$gray1000" textAlign="center">
                            Takes about a minute
                        </BodySmallRegular>
                    )}
                </YStack>
            </YStack>
        </ScreenContainer>
    );
};

export default IncomeSplitConfirmation;
